<template>
  <div
    ref="task"
    class="tasks-item"
    v-bind:class="['tasks-item-state-' + task.state,
                  !(task.permissions.COMPLETE || task.permissions.ASSIGN) ?'tasks-item-perm-no-open' :'']"
    style="display: flex; align-items: last baseline; justify-content: space-between"
    @dblclick.stop="canUpdate || task.permissions.ASSIGN || task.permissions.SCHEDULE ? edit(canUpdate ? 'name' : 'user-btn', 'div') : ''"
    >
      <div style="min-width: max-content">
        <i class="clickable icon-minus-sign text-red"
            v-if="!readonly && task.permissions.DELETE"
            @click.stop="remove()"></i>
        <i class="icon-lock"
            v-if="!readonly && !task.permissions.DELETE"
            ></i>
        <input type=checkbox
               :disabled="readonly || task.state == 'PLANNED' || !task.permissions.COMPLETE"
               v-model="task.state"
               true-value="COMPLETE"
               false-value="TODO"
               >
      </div>

      <div style="display: flex;flex-flow: row wrap; flex-grow: 1"
           v-bind:style="{'flex-direction': task.editing ? 'column': 'row'}"
        >
        <!-- first row -->
        <div style="padding: 3px; ">
          <span v-if="(!task.editing || !canUpdate) && task.date" class="rm-task-date" style="">{{ formattedDate }}</span>
          <span v-if="!task.editing || !canUpdate">{{ task.name }}</span>
          <span v-if="!task.editing &&  task.assignee !== undefined" class="rm-task-assignee">
              {{ task.assignee.username }}
          </span>
          <input type=text
                 class=rm-task-name
                 v-model="task.name"
                 v-if="task.editing && canUpdate"
                 @keyup.enter="unedit('keyup')"
                 @keyup.escape="unedit('keyup')"
                 @dblclick.stop.prevent
                 >
        </div>

        <!-- second row: assignee -->
        <div v-if="task.editing && task.permissions.ASSIGN && (task.assignee !== undefined || showUserSearch)"
             style="padding: 3px; text-align: right">
            <span v-if="task.assignee !== undefined" style="padding-right: 5px">
              <a class="clickable icon-user rm-task-user-btn" tabindex=-1
                 @click="task.assignee = undefined; focus(task.permissions.DELETE ? 'name' : 'user-btn')"
              ></a>
              <span class="rm-task-assignee">
                {{ task.assignee.username }}
              </span>
            </span>
            <UsermTypeahead
              v-if="showUserSearch"
              v-model="task.assignee"
              @enter="unedit('enter')"
              @escape="unedit('escape')"
              :usermBaseUrl="usermBaseUrl"
              :i18n="i18n"
              :fixedItems="fixedItems"
            >
            </UsermTypeahead>
        </div>
        <div
          v-if="task.editing && task.permissions.ASSIGN"
          style="padding: 3px; align-self: flex-end">
          <button class="btn btn-mini rm-task-user-btn"
             title="Assign"
             v-if="task.editing && task.assignee === undefined && !showUserSearch"
             @click.stop="assign()"
            >
              <i class="clickable icon-plus"></i>
              <i class="clickable icon-user"></i>
          </button>
        </div>

        <!-- third row: date -->
        <div v-if="task.editing && (task.permissions.SCHEDULE || !!task.date)"
             style="padding: 3px; text-align: right">
            <span v-if="!!task.date" style="padding-right: 5px">
              <a class="clickable icon-calendar" tabindex=-1
                 v-if="task.permissions.SCHEDULE"
                 @click="$set(task, 'date', undefined);  focus(task.permissions.DELETE ? 'name' : 'date-btn')"
              ></a>
              <span class="rm-task-date">
                {{ formattedDate }}
              </span>
            </span>
        </div>

      </div>

      <div style="min-width: max-content; align-self: auto;padding: 0 5px 0 0;">
        <i class="icon-align-justify rm-task-handle"
            v-if="!readonly"
            ></i>
      </div>

      <div v-if="debug" style="position: absolute;margin-top: -10px;color: lightslategray; font-size: smaller">
          <span>{{ [...Object.entries(task.permissions), ['UPDATE', canUpdate]].filter(e => e[1]).map(e => e[0][0]).join(" ") }}</span>
      </div>
  </div>
</template>

<script>
import UsermTypeahead from './userm-typeahead.vue';
import utils from '@cob/ui-genesis/cob.utils.js';

var focusWatcherInterval;

export default {
  components: { UsermTypeahead },

  model: {
    prop: 'task',
    event: 'change'
  },

  props: {
    task: Object,
    fieldPermissions: { type: Object, default: () => ({}) },
    debug: Boolean,
    readonly: Boolean,
    usermBaseUrl:String,
    i18n: Object,
    fixedItems: {
      required: false,
      type: Array
    }
  },

  data () {
      return {
        showUserSearch : false,
        // used just to feed the DatePicker, the value then is converted a DD/MM/YYYY string
        pickerDate: null,

        modelConfig: {
          timeAdjust: '00:00:00' //we do this to make selected dates have a specific time
        }
      }
  },

  computed: {
    canUpdate: function(){
      return this.fieldPermissions.CREATE && this.task.permissions.DELETE
    },

    formattedDate: function(){
      return utils.time.getLocalDateFromTimeStamp(this.task.date, this.i18n.locale)
    }
  },

  created() {
    if(this.task.editing){
      this.edit('name', 'create')
    }
  },

  methods: {
    focus: function(what){
      window.console.debug('TASK', 'focus', this.task.id, what);
      this.$nextTick(() => {
        const t = this.$refs.task;
        // we protect against non-existing elements
        // because fast task deletes can be interpreted as double-clicks
        // and then the task is no longer there
        // I think it's a consequence of our settings on draggable for better mobile experience.
        if(t){
          const t2 = this.$refs.task.querySelector(".rm-task-" + what)
          t2 && t2.focus()
        }
      })
    },

    edit: function(what, from){
      window.console.debug('TASK', 'edit', this.task.id, what, from);
      if(!this.readonly && (this.task.permissions.DELETE || this.task.permissions.ASSIGN)){
        // unedit all other tasks
        this.$emit('editTask')
        if(focusWatcherInterval){
          clearInterval(focusWatcherInterval);
          focusWatcherInterval = undefined;
        }

        this.$set(this.task, 'editing', true);
        this.focus(what);

        // we want to unedit when focusing anything outside the current task container
        focusWatcherInterval = setInterval( () => {
          //window.console.debug('TASK', 'activeElement', document.activeElement)
          //window.console.debug('TASK', this.$refs.task, this.$refs.task.contains(document.activeElement))
          // does the focus still reside inside de task elements?
          if (this.$refs.task === undefined
              || !this.$refs.task.contains(document.activeElement)) {

            clearInterval(focusWatcherInterval);
            focusWatcherInterval = undefined;

            this.unedit('focusWatcher');
          }
        }, 1000)
      }
    },

    unedit: function(from){
      window.console.debug('TASK', 'unedit', this.task.id, from);
      this.showUserSearch = false;
      // para que algum click que tenha acontecido noutro elemento
      // aconteça primeiro, porque passar do SPAN para o INPUT pode
      // mudar o tamanho e depois o click ia falhar o alvo
      setTimeout(() => {
        this.$set(this.task, 'editing', false);
        if(this.task.name.trim() === ""){
          this.remove();
        }
      }, 100)
    },

    assign: function(){
      this.showUserSearch = true;
      this.focus('assignee');
    },

    remove: function(){
      this.$emit('deleteTask');
    }
  }
}
</script>

<style>
</style>

<!-- vim: set sw=2 ts=2 et : -->
